import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../../../../components/ui';
import { TimesheetModal } from './TimesheetModal';
import { formatDate, formatCurrency } from '../../../../utils/formatters/statusFormatters';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import { TransformedProject } from '../hooks/useProjects';
import { TimesheetEntry, TimesheetSummary } from '../hooks/useTimesheets';
import { Person } from '../hooks/usePeople';

interface Client {
  id: string;
  name: string;
}

interface EmployeeRecord {
  id: string;
  name: string;
  email?: string;
  role?: string;
  hourlyRate?: number;
}

interface EmployeeGroup {
  employee: EmployeeRecord;
  entries: TimesheetEntry[];
}

interface Filters {
  startDate: string;
  endDate: string;
  employeeId: string;
}

interface Props {
  projects: TransformedProject[];
  people: Person[];
  timesheets: TimesheetEntry[];
  summary: TimesheetSummary;
  loading: boolean;
  clientFilter?: string | null;
  selectedProject?: TransformedProject | null;
  onClientFilterChange?: (clientId: string | null) => void;
  onProjectFilterChange: (projectId: string | null) => void;
  onFilterChange?: (filters: Filters) => void;
  onAddTimesheet: (data: Record<string, unknown>) => void;
  onEditTimesheet: (timesheetId: string, data: Record<string, unknown>) => void;
  onDeleteTimesheet: (timesheetId: string) => void;
  clients?: Client[];
}

export const TimeTrackingView: React.FC<Props> = ({
  projects,
  people,
  timesheets,
  summary,
  loading,
  clientFilter,
  selectedProject,
  onClientFilterChange,
  onProjectFilterChange,
  onFilterChange,
  onAddTimesheet,
  onEditTimesheet,
  onDeleteTimesheet,
  clients = []
}) => {
  const { isAWSUser } = useModuleAccess();
  const isAWS = isAWSUser();
  const [filters, setFilters] = useState<Filters>({
    startDate: '',
    endDate: '',
    employeeId: ''
  });

  const [showTimesheetModal, setShowTimesheetModal] = useState(false);
  const [editingTimesheet, setEditingTimesheet] = useState<TimesheetEntry | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeRecord | null>(null);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearFilters = () => {
    const emptyFilters: Filters = { startDate: '', endDate: '', employeeId: '' };
    setFilters(emptyFilters);
    if (onFilterChange) {
      onFilterChange(emptyFilters);
    }
  };

  const handleAddTimesheet = (employee: EmployeeRecord | null = null) => {
    setSelectedEmployee(employee);
    setEditingTimesheet(null);
    setShowTimesheetModal(true);
  };

  const handleEditTimesheet = (timesheet: TimesheetEntry) => {
    setEditingTimesheet(timesheet);
    setSelectedEmployee(null);
    setShowTimesheetModal(true);
  };

  const handleTimesheetSubmit = async (timesheetData: Record<string, unknown>) => {
    if (editingTimesheet) {
      await onEditTimesheet(editingTimesheet.id, timesheetData);
    } else {
      await onAddTimesheet(timesheetData);
    }
    setShowTimesheetModal(false);
    setEditingTimesheet(null);
    setSelectedEmployee(null);
  };

  const employeeTimesheets = React.useMemo<EmployeeGroup[]>(() => {
    const grouped: Record<string, EmployeeGroup> = {};

    timesheets.forEach(ts => {
      const empId = ts.employeeId ?? ts.employee_id ?? '';
      if (!grouped[empId]) {
        grouped[empId] = {
          employee: {
            id: empId,
            name: ts.employeeName ?? ts.employee_name ?? '',
            email: ts.employeeEmail ?? ts.employee_email,
            role: ts.role,
            hourlyRate: ts.hourlyRate ?? ts.hourly_rate
          },
          entries: []
        };
      }
      grouped[empId].entries.push(ts);
    });

    return Object.values(grouped);
  }, [timesheets]);

  const allEmployees = React.useMemo<EmployeeRecord[]>(() => {
    const empMap = new Map<string, EmployeeRecord>();

    people.forEach(p => {
      const empId = p.employeeId ?? p.employee_id ?? p.id;
      empMap.set(empId, {
        id: empId,
        name: p.name,
        email: p.email,
        role: p.role,
        hourlyRate: p.hourlyRate ?? p.hourly_rate
      });
    });

    timesheets.forEach(ts => {
      const empId = ts.employeeId ?? ts.employee_id ?? '';
      if (empId && !empMap.has(empId)) {
        empMap.set(empId, {
          id: empId,
          name: ts.employeeName ?? ts.employee_name ?? '',
          email: ts.employeeEmail ?? ts.employee_email,
          role: ts.role,
          hourlyRate: ts.hourlyRate ?? ts.hourly_rate
        });
      }
    });

    return Array.from(empMap.values());
  }, [people, timesheets]);

  const employeeCount = allEmployees.length;

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent>
          <div className={`grid gap-4 mb-4 ${isAWS ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {!isAWS && onClientFilterChange && (
              <div>
                <label htmlFor="filter-by-client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Filter by Client (optional)
                </label>
                <select id="filter-by-client"
                  value={clientFilter ?? ''}
                  onChange={(e) => onClientFilterChange(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">All Clients</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label htmlFor="select-project" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Project (optional)
              </label>
              <select id="select-project"
                value={selectedProject?.id ?? ''}
                onChange={(e) => onProjectFilterChange(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Hours</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {summary?.total_hours ?? summary?.totalHours ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {formatCurrency(summary?.total_cost ?? summary?.totalCost ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Entries</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {summary?.entry_count ?? summary?.entryCount ?? 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Employees</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
              {employeeCount}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Date Filters and Actions */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Start Date
              </label>
              <input id="start-date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                End Date
              </label>
              <input id="end-date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            {allEmployees.length > 0 && (
              <div className="flex-1 min-w-[200px]">
                <label htmlFor="employee" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Employee
                </label>
                <select id="employee"
                  value={filters.employeeId}
                  onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">All Employees</option>
                  {allEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={clearFilters}>
                Clear Filters
              </Button>
              <Button onClick={() => handleAddTimesheet()}>
                Add Entry
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Employee Cards */}
      {loading ? (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          </CardContent>
        </Card>
      ) : employeeTimesheets.length === 0 && allEmployees.length === 0 ? (
        <Card>
          <CardContent>
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No employees or timesheet entries found
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {allEmployees.map(employee => {
            const employeeData = employeeTimesheets.find(et => et.employee.id === employee.id);
            const entries = employeeData?.entries ?? [];

            return (
              <Card key={employee.id}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                      {employee.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{employee.name}</CardTitle>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{employee.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(employee.hourlyRate ?? 0)}/hr
                    </span>
                    <Button size="sm" onClick={() => handleAddTimesheet(employee)}>
                      Add Entry
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {entries.length === 0 ? (
                    <p className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No timesheet entries found for {employee.name}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {entries.map(entry => (
                        <div
                          key={entry.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatDate(entry.dateWorked ?? entry.date_worked)}
                              </span>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {entry.hours}h
                              </span>
                              {entry.billable ? (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300">
                                  Billable
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300">
                                  Non-billable
                                </span>
                              )}
                              {entry.cost && (
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(entry.cost)}
                                </span>
                              )}
                            </div>
                            {entry.notes && (
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {entry.notes}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditTimesheet(entry)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => onDeleteTimesheet(entry.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Timesheet Modal */}
      <TimesheetModal
        isOpen={showTimesheetModal}
        onClose={() => {
          setShowTimesheetModal(false);
          setEditingTimesheet(null);
          setSelectedEmployee(null);
        }}
        onSubmit={handleTimesheetSubmit}
        timesheet={editingTimesheet}
        employees={allEmployees as Person[]}
        milestones={selectedProject?.milestones ?? []}
        loading={false}
        selectedEmployeeId={selectedEmployee?.id}
        projects={projects}
        selectedProjectId={selectedProject?.id}
      />
    </div>
  );
};

export default TimeTrackingView;
